import { Injectable } from '@angular/core';
import { ChatSession, GenerationConfig, GenerativeModel, GoogleGenerativeAI, SchemaType } from '@google/generative-ai';
import { marked, Tokens } from 'marked';
import { BehaviorSubject, Observable } from 'rxjs';
import { AddHistoryService } from 'src/portal/ai-proxy-api/services/add-history.service';
import { ChatEnrichmentConfigService } from '../configs/chat-enrichment.config';
import { INSTRUCTIONS } from './instructions.constants';

export interface ChatMessage {
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isTyping?: boolean;
  companies?: CompanyDomain[];
}

export interface CompanyDomain {
  company_name: string;
  domain: string;
}

export enum ChatCommand {
  FindEmployees = 'Find profiles of employees',
  FindCustomers = 'Find customers of products',
  AnalyzeTechStacks = 'Analyze tech stacks for company',
  RecommendSoftware = 'Recommend software',
  BenchmarkCompanies = 'Benchmark companies',
  GeneralMode = 'General Mode',
  ProductAnalysis = 'Product Analysis',
}

interface CompanyDomainsData {
  company_domains?: unknown;
  [key: string]: unknown;
}

interface ModelConfig {
  systemInstruction: string;
  temperature?: number;
  tools?: object[];
  thinkingConfig?: object;
}

@Injectable({ providedIn: 'root' })
export class AiChatService {
  private readonly apiKey = 'AIzaSyCEQ-DkCwkA3ksRpzaNsgB8m1eEfkLADhA';
  private genAI: GoogleGenerativeAI;
  private model: GenerativeModel;
  private chatSession: ChatSession;

  private chatHistorySubject = new BehaviorSubject<ChatMessage[]>([]);
  public chatHistory$: Observable<ChatMessage[]> = this.chatHistorySubject.asObservable();
  private chatHistory: ChatMessage[] = [];

  public hiFlag = false;
  public selectedMode: ChatCommand = ChatCommand.GeneralMode;
  public companyDomains: CompanyDomain[] = [];
  public isLoading = false;

  constructor(private history:AddHistoryService, private enrichmentConfigService: ChatEnrichmentConfigService) {
    this.genAI = new GoogleGenerativeAI(this.apiKey);
    this.initializeModel({
      systemInstruction: INSTRUCTIONS.systemInstructionMain,
      thinkingConfig:{thinkingBudget: 0},
    });
    this.setupMarkedRenderer();
  }

  private setupMarkedRenderer(): void {
    const renderer = new marked.Renderer();

    renderer.link = function({ href, title, tokens }: Tokens.Link): string {
      const text = marked.parser(tokens);

      // Проверка на внутреннюю или относительную ссылку
      const isInternal = href.startsWith('/') || !href.startsWith('http');

      if (isInternal) {
        // Экранируем кавычки, если нужно
        return `<a href="${href}" role="button" class="router-link">${text}</a>`;
      }

      // Внешние ссылки
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}"${titleAttr} target="_blank" role="button" rel="noopener noreferrer">${text}</a>`;
    };
    renderer.code = ({ text, lang }) => {
      if (lang === 'json') {
        try {
          const parsedJson = JSON.parse(text);
          const formattedJson = JSON.stringify(parsedJson, null, 2);
          return `
                  <details class="mt-2 mb-2">
                  <summary><b>Expand / Collapse JSON</b></summary>
                  <pre><code class="language-json">${formattedJson}</code></pre>
                  </details>`;
        } catch (e) {
          return `<pre><code>${text}</code></pre>`;
        }
      }
      return `${text}`;
    };
    marked.setOptions({ renderer });
  }

  private updateChatHistory(): void {
    this.chatHistorySubject.next([...this.chatHistory]);
  }

  private addMessage(message: ChatMessage): void {
    this.chatHistory.push(message);
    this.updateChatHistory();
  }

  private initializeModel(config: ModelConfig): void {
    this.model = this.genAI.getGenerativeModel({
      //model: 'gemini-2.5-flash',
      model: 'gemini-2.0-flash',
      //model: 'gemini-2.0-flash-lite',
      //model: "gemini-2.5-flash-lite-preview-06-17",
      //model: 'gemini-2.5-pro',
      systemInstruction: config.systemInstruction,
      generationConfig:{
        temperature: config.temperature ?? 1,
      }
    });

    this.chatSession = this.model.startChat({
      generationConfig: {
        temperature: config.temperature ?? 1,
        topP: 0.95,
        topK: 40,
        maxOutputTokens: 8192,
        responseMimeType: 'text/plain',

      },
      history: [],
    });
  }

  private configureMode(message: string): string {

    const modeConfigs: Record<ChatCommand, ModelConfig> = {
      [ChatCommand.FindEmployees]: {
        systemInstruction: INSTRUCTIONS.systemIstructionEmployee,
        temperature: 0.7,
        thinkingConfig: {
          thinkingBudget: 0,
        },
      },
      [ChatCommand.FindCustomers]: {
        systemInstruction: INSTRUCTIONS.systemInstructionCompaniesUseProduct,
        temperature: 0.7,
      },
      [ChatCommand.AnalyzeTechStacks]: {

        systemInstruction: INSTRUCTIONS.systemInstructionCompanySoftwareAnalysis,
        temperature: 0.7,
        thinkingConfig:{
          thinkingBudget: 0,
        },
      },
      [ChatCommand.RecommendSoftware]: {
        systemInstruction: INSTRUCTIONS.systemInstructionMain,
      },
      [ChatCommand.BenchmarkCompanies]: {
        systemInstruction: INSTRUCTIONS.systemInstructionMain,
      },
      [ChatCommand.GeneralMode]: {
        systemInstruction: INSTRUCTIONS.systemInstructionMain,
      },
      [ChatCommand.ProductAnalysis]: {
        systemInstruction: INSTRUCTIONS.systemInstructionProduct,
        temperature: 0.7,
      }
    };

    const command = message as ChatCommand;



    if (modeConfigs[command]) {
      this.selectedMode = command;
      this.initializeModel(modeConfigs[command]);
      return command;
    }

    return message;
  }

  async sendMessage(message: string): Promise<void> {
    this.isLoading = true;
    const processedMessage = this.configureMode(message.trim());
    const botMessage = this.createBotMessage();

    if (!this.isGreeting(processedMessage)) {
      this.handleUserMessage(processedMessage, botMessage);

      if (this.shouldEnrichWithData(message)) {
        await this.enrichWithDataByMode(message);
      }

    } else {
      this.hiFlag = true;
      this.addMessage(botMessage);
    }

    await this.sendToChat(processedMessage, botMessage);
  }

  private createBotMessage(): ChatMessage {
    return {
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      isTyping: true,
    };
  }

  private isGreeting(message: string): boolean {
    return message.startsWith('Hi!');
  }

  private handleUserMessage(userMessage: string, botMessage: ChatMessage): void {
    this.addMessage({
      text: userMessage,
      sender: 'user',
      timestamp: new Date(),
    });
    this.addMessage(botMessage);
  }

  private shouldEnrichWithData(message: string): boolean {
    return (
      (this.selectedMode === ChatCommand.AnalyzeTechStacks && message !== "Analyze tech stacks for company") ||
      (this.selectedMode === ChatCommand.FindCustomers && message !== "Find customers of products") ||
      (this.selectedMode === ChatCommand.FindEmployees && message !== "Find profiles of employees")
    );
  }




  private async enrichWithDataByMode(message: string): Promise<void> {
    const config = this.enrichmentConfigService.config[this.selectedMode];
    if (!config) return;

    //const prompt = config.promptPrefix +' '+ message;
    const prompt = message;
    const [dataResponse, geminiData] = await Promise.all([
      config.fetchDataFn(prompt),
      config.geminiFn(config.systemInstruction, prompt)
    ]);

    const data = dataResponse?.data ?? [];

    this.updateSystemInstruction(data, geminiData, config.systemInstruction, message);
  }

  private updateSystemInstruction(
    data: any[],
    geminiData: any,
    systemInstructionText: string,
    message: string
  ): void {
    const currentInstruction = this.chatSession.params.systemInstruction;
    const isFindProfiles = this.selectedMode === ChatCommand.FindEmployees;
    if (data?.length > 0) {
      const additionalText = isFindProfiles
        ? `## User profile data received for: ${message}\n +
          ######## - Buyercaddy profiles: ${JSON.stringify(data, null, 2)}\n +
          ######## - Other profiles:\n${geminiData}\n+
          ## Show this datasources in separate tables, use columns from the data, also add a column with the source name.
          ## Buyercaddy datasource data priority over Gemini profiles.
          ## For column 'expertises' rename column to 'Core Skills', and in data, Group related skills into categories (e.g., 'Programming', 'Design', 'Analytics') combine related skills and shorten the text to make it concise and easy to read. Apply these changes to all data in the column.
          ## For column 'jobTitle' rename to 'Title'.
          ## For column 'jobFunction' rename to 'Department'.
          ## For column 'companyCity' rename to 'Location'.
          ## For column 'name' Link Creation: For any row in the buyercaddy data that contains an id field, create a hyperlink to the username in the format: /contact/{id}. Example: If the id is prof-shli-61d73e2f009806b48eaec676c3f37a8c, the link should be contact/prof-shli-61d73e2f009806b48eaec676c3f37a8c.
          ## For column 'name' Need to make a link from the name using id (name must be the link text), example contact/prof-shli-61d73e2f009806b48eaec676c3f37a8c, in the format: /contact/{id}
          ## For column 'socialLinks' need to samely sign the link, for example, LinkedIn (url use from data if it exists) - Remove commas between links
          ## Always show buyercaddy data source in a separate table

          ## Instruction for Handling Buyercaddy Data
            Source Restriction: Always and only use data from "buyercaddy" for responses related to this data source.
            Link Creation: For any row in the buyercaddy data that contains an id field, create a hyperlink to the username in the format: /contact/{id}.
            Example: If the id is prof-shli-61d73e2f009806b48eaec676c3f37a8c, the link should be contact/prof-shli-61d73e2f009806b48eaec676c3f37a8c.
            Consistency: This rule must be applied every time buyercaddy data is processed, without exception.
            Explanation of Changes:
            Clarity: The instruction is broken into clear, actionable points using bullet points for better readability.
            Emphasis on "Always": Explicitly stating "Always and only" reinforces that no other data source should be used for buyercaddy-related queries.
            Specificity: The link creation process is described step-by-step, with an example to avoid ambiguity.
            Consistency: Adding "without exception" ensures the AI understands this is a mandatory rule.`

        : `This is an additional data source from BuyerCaddy for the company: ${message}\n +
          buyercaddy: ${JSON.stringify(data, null, 2)}\n +
          For this additional source, the source is specified within the dataset itself (source field): \n ${geminiData}\n +
          ## This is a dynamic data source, for each user prompt there is always data. ...`;

      this.chatSession.params.systemInstruction = this.mergeInstructions(
        currentInstruction,
        systemInstructionText + additionalText
      );
    } else {
      const additionalText = isFindProfiles
        ? '## No user profile data found!\n' +
          'Try refining your prompt, e.g. include company name or domain.\n' +
          'Do not use Other profiles if BuyerCaddy returned nothing.'
        : 'This is an additional data source from BuyerCaddy Datasource BuyerCaddy: [] \n' +
          '-## No buyercaddy data found! Try adding data from other sources available to you.\n' +
          '-## NEVER USE SOURCE AS BUYERCADDY WHEN NO BUYERCADDY DATA FOUND !!!';

      this.chatSession.params.systemInstruction = this.mergeInstructions(
        currentInstruction,
        systemInstructionText + additionalText
      );
    }
  }

  private mergeInstructions(currentInstruction: any, additionalText: string) {
    if (typeof currentInstruction === 'string') {
      return {
        role: 'system',
        parts: [{ text: currentInstruction }, { text: additionalText }]
      };
    }

    if (currentInstruction && 'parts' in currentInstruction && Array.isArray(currentInstruction.parts)) {
      return {
        ...currentInstruction,
        parts: [{ text: additionalText }]
      };
    }

    return {
      role: 'system',
      parts: [{ text: additionalText }]
    };
  }

  private async sendToChat(processedMessage: string, botMessage: ChatMessage): Promise<void> {
    try {
      this.chatSession.params.history = [];
      const result = await this.chatSession.sendMessageStream(processedMessage);
      await this.processStreamResponse(processedMessage, result.stream, botMessage);
    } catch (error) {
      this.handleError(botMessage, error);
    } finally {
      this.isLoading = false;
    }
  }

  private async processStreamResponse(
    processedMessage: string,
    stream: AsyncIterable<any>,
    botMessage: ChatMessage
  ): Promise<void> {
    let accumulatedText = '';

    // Отображаем текст в реальном времени из стрима
    for await (const chunk of stream) {
      accumulatedText += chunk.text().replace('```markdown', '').replace('```', '').replace('```\n', '').replace('markdown','');
      botMessage.text =  await marked(accumulatedText);
      this.updateChatHistory();
    }
    //сохранение истории на сервер
    this.saveHistory(processedMessage, accumulatedText);
    // После завершения стрима проверяем весь текст на JSON с company_domains
    const companyJson = this.extractAndProcessJSON(accumulatedText);
    if (companyJson) {
      accumulatedText =
        accumulatedText.replace(companyJson, '') + '\n[[COMPANY_CARD]]';
      botMessage.text = await marked(accumulatedText);
      botMessage.companies = this.extractCompaniesFromJson(companyJson);
    }
    botMessage.text = await marked(accumulatedText);
    botMessage.isTyping = false;

    //сохранение истории локально
    this.updateChatHistory();
  }

  private saveHistory(question:string,response:string):void{
    this.history.addHistory({body:{message:question,response:response}}).subscribe();
  }

  private extractAndProcessJSON(responseText: string): string | undefined {
    const extractJSON = (str: string): string | null => {
      let firstOpen = str.indexOf('{');
      let firstClose, candidate;

      while (firstOpen !== -1) {
        firstClose = str.lastIndexOf('}');

        if (firstClose <= firstOpen) {
          return null;
        }

        while (firstClose > firstOpen) {
          candidate = str.substring(firstOpen, firstClose + 1);

          try {
            const parsed = JSON.parse(candidate);
            // Проверяем наличие нужного ключа в JSON
            if (parsed && parsed.company_domains && Array.isArray(parsed.company_domains)) {
              return candidate;
            }
          } catch (e) {
            // Игнорируем ошибки парсинга и продолжаем
          }

          firstClose = str.lastIndexOf('}', firstClose - 1);
        }

        firstOpen = str.indexOf('{', firstOpen + 1);
      }

      return null;
    };

    try {
      // Сначала проверяем markdown-блок
      const markdownJsonPattern = /json\s*([\s\S]*?)\s*/g;
      let match;
      while ((match = markdownJsonPattern.exec(responseText)) !== null) {
        const candidate = match[1];
        if (this.validateAndReturnJson(candidate)) {
          return candidate;
        }
      }

      // Затем используем надежный подход с парсингом
      return extractJSON(responseText) || undefined;
    } catch (error) {
      console.error('Error in JSON extraction:', error);
      return undefined;
    }
  }


  private validateAndReturnJson(jsonString: string): string | undefined {
    try {
      const jsonData = JSON.parse(jsonString);

      if (Array.isArray(jsonData)) {
        // Если верхний уровень — массив, проверим объекты на наличие company_domains
        for (const item of jsonData) {
          if (item.company_domains && Array.isArray(item.company_domains)) {
            return jsonString;
          }
        }
      } else if (jsonData.company_domains && Array.isArray(jsonData.company_domains)) {
        // Проверяем наличие ключа в объекте
        return jsonString;
      }

      console.warn('JSON does not contain valid company_domains');
      return undefined;
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return undefined;
    }
  }

  private extractCompaniesFromJson(jsonString: string): CompanyDomain[] {
    try {
      const jsonData: CompanyDomainsData = JSON.parse(jsonString);
      if (jsonData.company_domains && Array.isArray(jsonData.company_domains)) {
        return jsonData.company_domains as CompanyDomain[];
      }
      return [];
    } catch (error) {
      console.error('Error parsing company JSON:', error);
      return [];
    }
  }

  private handleError(botMessage: ChatMessage, error: unknown): void {
    console.error('Error:', error);
    botMessage.isTyping = false;
    botMessage.text = '<em>Something went wrong. Please try again.</em>';
    this.updateChatHistory();
  }

  clearChatHistory(): void {
    this.chatHistory = [];
    this.updateChatHistory();
  }

  async refreshPrompts() {
    const genAI = new GoogleGenerativeAI(this.apiKey);
    const model = genAI.getGenerativeModel({
      model: "gemini-2.0-flash",
     // model: "gemini-2.5-pro-exp-03-25",
      systemInstruction: "You are a professional Prompt Engineer, the best in the world. The user will send an instruction for an AI. Your task: thoroughly analyze this instruction and create 10 concise, original, and engaging prompts (max 100 characters each). Always include real companies or famous individuals, but strictly avoid using any companies or people mentioned in the instruction provided by the user. Your prompts should be clever, witty, and unusual. Only Software products, Any Companies" + ' Use topic: '+ this.selectedMode,
    });

    const generationConfig:GenerationConfig = {
      temperature: 1,
      topP: 0.95,
      topK: 64,
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: SchemaType.OBJECT,
        properties: {
          prompts: {
            type: SchemaType.ARRAY,
            items: {
              type: SchemaType.STRING
            }
          }
        },
        required: ["prompts"]
      },
    };

    try {
      const chatSession = model.startChat({
        generationConfig,
        history: [],
      });

      const result = await chatSession.sendMessage(this.model.systemInstruction.parts.map(part => part.text).join(""));
      const responseText = result.response.text();
      const responseJson = JSON.parse(responseText);

      return responseJson.prompts || [];
    } catch (error) {
      console.error("Error generating prompts:", error);
      return [];
    }
  }
}